import React, { useEffect, useState } from 'react'

import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'

import { ApplicationState } from '@store/types'
import { Button } from '@interco/inter-ui/components/Button'
import { StepRefin } from '@utils/enums'
import Tag from '@utils/Tag'
import { Page } from '@templates'
import { ColorWrapper, TextParagraph } from '@atoms'
import { Colors } from '@utils/Colors'
import routes from '@routes/routes'
import {
  setBancoSelecionado,
  setTipoConta,
  setAgencia,
  setContaDadosBancarios,
  setDigito,
  bancosRequest,
  clearAccountFields,
} from '@store/refinanciamento/dadosBancarios/actions'
import {
  clearBankAccountCorrection,
  postDadosPessoaisRequest,
  setHasDadosBancarios,
} from '@store/refinanciamento/dadosPessoais/actions'
import { trackingRefin } from '@store/refinanciamento/simulacao/actions'
import { getNextRoute } from '@store/refinanciamento/dadosPessoais/utils'
import { Banco } from '@store/refinanciamento/dadosBancarios/types'
import { hasCompleteDadosBancarios } from '@utils/customerDataCompleteness'
import { isSameRejectedBankAccount } from '@store/refinanciamento/dadosPessoais/isSameRejectedBankAccount'
import {
  INVALID_MODALITY_ACCOUNT_SUBTITLE,
  toBankData,
} from '@store/refinanciamento/dadosPessoais/bankAccountCorrection'

import { DadosBancariosFields } from './DadosBancariosFields'
import {
  buildDadosBancariosLocal,
  DadosBancariosLocal,
  EMPTY_DADOS_BANCARIOS_LOCAL,
  isDadosBancariosFormComplete,
} from './dadosBancariosForm'

const DadosBancarios = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const location = useLocation()
  const fromInformacoesPessoais = location.state?.fromInformacoesPessoais || false
  const {
    tipoContaSelecionado,
    bancoSelecionado,
    agencia,
    contaDadosBancarios,
    digito,
    isBankAccountEditable,
  } = useSelector((state: ApplicationState) => state.refinanciamento.dadosBancarios, shallowEqual)

  const [closeAlert, setCloseAlert] = useState(false)
  const [bancos, setBancos] = useState<Banco[]>([])
  const { convenioCodigo } = useSelector(
    (state: ApplicationState) => state.refinanciamento.contratos,
  )
  const { postDadosPessoaisLoading, correctionMode, rejectedBankAccount } = useSelector(
    (state: ApplicationState) => state.refinanciamento.dadosPessoais,
  )
  const isModalityCorrectionPath = Boolean(correctionMode)
  const [localDadosBancarios, setLocalDadosBancarios] = useState<DadosBancariosLocal>(() =>
    buildDadosBancariosLocal(
      {
        bancoSelecionado: bancoSelecionado ?? '',
        tipoContaSelecionado: tipoContaSelecionado ?? '',
        agencia: agencia ?? '',
        contaDadosBancarios: contaDadosBancarios ?? '',
        digito: digito ?? '',
      },
      isModalityCorrectionPath,
    ),
  )
  const loadingContratacao = useSelector(
    (state: ApplicationState) => state.refinanciamento.contratacao.loadingContratacao,
  )
  const [isConfirming, setIsConfirming] = useState(false)

  const isBankFieldsEditable =
    isModalityCorrectionPath ||
    Boolean(isBankAccountEditable) ||
    !hasCompleteDadosBancarios({
      bancoSelecionado,
      tipoContaSelecionado,
      agencia,
      contaDadosBancarios,
      digito,
    })
  const isRejectedAccount =
    isModalityCorrectionPath &&
    isSameRejectedBankAccount(toBankData(localDadosBancarios), rejectedBankAccount)
  const isConfirmRequestInFlight = postDadosPessoaisLoading || loadingContratacao || isConfirming

  const handleConfirmClick = () => {
    if (isRejectedAccount) {
      return
    }

    dispatch(trackingRefin(StepRefin.CLICK_DADOS_BANCARIOS_CONFIRMAR))
    dispatch(setBancoSelecionado(localDadosBancarios.bancoSelecionado))
    dispatch(setTipoConta(localDadosBancarios.tipoContaSelecionado))
    dispatch(setAgencia(localDadosBancarios.agencia))
    dispatch(setContaDadosBancarios(localDadosBancarios.contaDadosBancarios))
    dispatch(setDigito(localDadosBancarios.digito))

    Tag.enviarTagueamentoWeb({
      element_action: 'click button',
      element_name: 'confirmar',
      c_page: window.location.href,
      redirect_url: '',
      section_name: 'payrollloan:refin:bank_account',
      section: 'bank_account',
      sub_section: '',
      step: '',
      scenario: 'Cadastro de conta bancária',
    })
    dispatch(trackingRefin(StepRefin.DADOS_BANCARIOS))
    dispatch(setHasDadosBancarios(true))

    if (isModalityCorrectionPath) {
      setIsConfirming(true)
      dispatch(
        postDadosPessoaisRequest({
          callback: () => {
            dispatch(clearBankAccountCorrection())
            setIsConfirming(false)
            navigate(routes.CARREGAMENTO_SIMULACAO)
          },
          errorCallback: () => setIsConfirming(false),
          invalidModalityAccountCallback: () => setIsConfirming(false),
        }),
      )
      return
    }

    navigate(getNextRoute())
  }

  useEffect(() => {
    if (isModalityCorrectionPath) {
      return
    }

    setLocalDadosBancarios({
      bancoSelecionado: bancoSelecionado ?? '',
      tipoContaSelecionado: tipoContaSelecionado ?? '',
      agencia: agencia ?? '',
      contaDadosBancarios: contaDadosBancarios ?? '',
      digito: digito ?? '',
    })
  }, [
    bancoSelecionado,
    tipoContaSelecionado,
    agencia,
    contaDadosBancarios,
    digito,
    isModalityCorrectionPath,
  ])

  useEffect(() => {
    if (!isModalityCorrectionPath) {
      return
    }

    dispatch(clearAccountFields())
    dispatch(setHasDadosBancarios(false))
  }, [dispatch, isModalityCorrectionPath])

  useEffect(() => {
    if (!isModalityCorrectionPath) {
      return
    }

    setLocalDadosBancarios(EMPTY_DADOS_BANCARIOS_LOCAL)
  }, [isModalityCorrectionPath, rejectedBankAccount])

  useEffect(() => {
    Tag.enviarTagueamentoWeb({
      element_action: 'data_loaded',
      element_name: '',
      c_page: window.location.href,
      redirect_url: '',
      section_name: 'payrollloan:refin:bank_account',
      section: 'bank_account',
      sub_section: '',
      step: '',
      scenario: 'Cadastro de conta bancária',
    })
  }, [])

  useEffect(() => {
    dispatch(
      bancosRequest({
        saveToState: false,
        callback: (bancosList) => {
          if (bancosList) {
            setBancos(bancosList)
          }
        },
      }),
    )
  }, [dispatch])

  return (
    <Page
      stickyContainerFooterBottom="30px"
      positionFooter="sticky"
      stickyFooter={
        <>
          <Button
            onClick={handleConfirmClick}
            disabled={
              !isDadosBancariosFormComplete(localDadosBancarios) ||
              isConfirmRequestInFlight ||
              isRejectedAccount
            }
            isLoading={isConfirmRequestInFlight}
          >
            Confirmar
          </Button>
          {fromInformacoesPessoais && !isModalityCorrectionPath && (
            <Button
              variant="secondary"
              fullWidth
              onClick={() => {
                dispatch(trackingRefin(StepRefin.CLICK_DADOS_BANCARIOS_CANCELAR))
                navigate(routes.INFORMACOES_PESSOAIS)
              }}
              style={{ marginTop: '12px' }}
            >
              Cancelar
            </Button>
          )}
        </>
      }
    >
      <div>
        <ColorWrapper
          color={Colors.GRAY500}
          fontWeight="600"
          margin={isModalityCorrectionPath ? '22px 0 8px 0' : '22px 0 16px 0'}
        >
          <TextParagraph variant="headline-h2">
            Cadastre uma conta bancária para depósito
          </TextParagraph>
        </ColorWrapper>
        {isModalityCorrectionPath ? (
          <ColorWrapper color={Colors.GRAY400} fontWeight="400" margin="0 0 16px 0">
            <TextParagraph variant="body-3">{INVALID_MODALITY_ACCOUNT_SUBTITLE}</TextParagraph>
          </ColorWrapper>
        ) : null}
      </div>
      <DadosBancariosFields
        convenioCodigo={convenioCodigo}
        closeAlert={closeAlert}
        onCloseInssAlert={() => setCloseAlert(true)}
        bancos={bancos}
        localDadosBancarios={localDadosBancarios}
        setLocalDadosBancarios={setLocalDadosBancarios}
        isBankFieldsEditable={isBankFieldsEditable && !isConfirmRequestInFlight}
      />
    </Page>
  )
}

export default DadosBancarios
